<script setup lang="ts">
import { computed, createApp, h } from "vue";

import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";

import { LUCIDE_ICONS } from "../../../shared/lucide-icons.ts";
import LucentIcon from "./LucentIcon.vue";

// ─── Inline icon support: `:icon-<name>:` ──────────────────────────
//
// Authors writing `sbi.ui.text` markdown can embed any icon from the
// curated 200-icon set inline with the `:icon-<name>:` syntax —
// e.g. ":icon-calendar:" or ":icon-trending-up:". The prefix is
// intentionally `:icon-` (not bare `:name:`) so the namespace doesn't
// collide with future emoji-shortcode support.
//
// Unknown names are left as literal text in the rendered output (no
// silent drop, no error toast — keeps the markdown forgiving). The
// validator-side enum is the same `LUCIDE_ICONS` set used everywhere
// else, so the briefing topic and `lucent_icon_search` cover this
// surface too.
//
// Rendering strategy: the markdown-it renderer emits the icon SVG
// inline as a string. Each unique icon name is rendered once, off-DOM,
// into a cached SVG string via a throwaway `createApp().mount()`; the
// cache survives for the lifetime of the module. DOMPurify with the
// `svg` profile passes the inline SVG through unchanged — the embed
// surface already trusts SVG output from the closed-enum
// `<LucentIcon>`, and the profile blocks `<script>` / event handlers
// as a defence-in-depth backstop. There is no post-mount pass and no
// per-occurrence Vue instance.

const LUCIDE_ICON_SET: ReadonlySet<string> = new Set<string>(LUCIDE_ICONS);

const SVG_CACHE: Map<string, string> = new Map();

/**
 * Render `<LucentIcon name="…" size="14">` once into a detached div,
 * capture its `innerHTML`, cache. Subsequent calls for the same name
 * return the cached string — O(1) for every reuse.
 *
 * Off-DOM mount is intentional: the icon library's Vue components
 * have no `mounted` side-effects, so a synchronous mount → read
 * outerHTML → unmount captures the same SVG the live render would
 * emit. We never reach for `document` from a non-browser context
 * (the embed runtime is exclusively client-side), so guarding for
 * `typeof document === "undefined"` isn't needed here.
 */
function svgFor(name: string): string {
  const cached = SVG_CACHE.get(name);
  if (cached !== undefined) return cached;
  const host = document.createElement("div");
  const app = createApp({ render: () => h(LucentIcon, { name, size: 14 }) });
  app.mount(host);
  const svg = host.innerHTML;
  app.unmount();
  SVG_CACHE.set(name, svg);
  return svg;
}

const md = new MarkdownIt({ html: false, linkify: true });

// Inline rule: parse `:icon-<name>:`. `name` is the kebab-case icon
// name (lowercase letters, digits, dashes). The leading `:icon-`
// prefix is mandatory — bare `:foo:` doesn't match, leaving room for
// future tokens (emoji shortcodes, footnote refs, etc.) without
// having to disambiguate at parse time.
md.inline.ruler.before("emphasis", "lucent_icon", (state, silent) => {
  const start = state.pos;
  const PREFIX = ":icon-";
  if (state.src.charCodeAt(start) !== 0x3a /* : */) return false;
  if (!state.src.startsWith(PREFIX, start)) return false;
  let end = start + PREFIX.length;
  while (end < state.src.length) {
    const ch = state.src.charCodeAt(end);
    const isLower = ch >= 0x61 && ch <= 0x7a; // a-z
    const isDigit = ch >= 0x30 && ch <= 0x39; // 0-9
    const isDash = ch === 0x2d; // -
    if (!isLower && !isDigit && !isDash) break;
    end++;
  }
  // Must close with a `:`; no name = no match.
  if (end === start + PREFIX.length) return false;
  if (end >= state.src.length || state.src.charCodeAt(end) !== 0x3a) return false;
  const name = state.src.slice(start + PREFIX.length, end);
  // Unknown name → bail; markdown-it will continue parsing the
  // surrounding text and the literal `:icon-foo:` ends up in the
  // output untouched. Authors see the typo and can fix it.
  if (!LUCIDE_ICON_SET.has(name)) return false;
  if (!silent) {
    const token = state.push("lucent_icon", "", 0);
    token.meta = { name };
    token.markup = state.src.slice(start, end + 1);
  }
  state.pos = end + 1;
  return true;
});

// Render rule: emit `<span class="lucent-md-icon">…SVG…</span>` with
// the icon's SVG inlined. The cache means each unique name pays the
// render cost once for the module's lifetime, not per occurrence.
md.renderer.rules.lucent_icon = (tokens, idx) => {
  const name = (tokens[idx]?.meta as { name?: string } | undefined)?.name;
  if (!name || !LUCIDE_ICON_SET.has(name)) return "";
  return `<span class="lucent-md-icon" aria-hidden="true">${svgFor(name)}</span>`;
};

const props = defineProps<{ markdown: string }>();

// markdown-it's html=false drops embedded HTML, but we still pipe through
// DOMPurify before v-html as defence in depth. The Worker is the security
// boundary; this is belt-and-braces for the browser-side render. The
// `svg` profile is opted in so the inline icon SVGs survive the
// sanitiser; the profile still strips `<script>` and event handlers.
const sanitised = computed(() =>
  DOMPurify.sanitize(md.render(props.markdown), {
    USE_PROFILES: { html: true, svg: true },
  }),
);
</script>

<template>
  <section class="text-card">
    <div class="prose" v-html="sanitised" />
  </section>
</template>

<style scoped>
.text-card {
  background: var(--card);
  /* Themer-facing: --lucent-card-border / --lucent-card-shadow /
     --lucent-card-pad. */
  border: var(--lucent-card-border, 1px solid var(--rule));
  border-radius: var(--radius);
  padding: var(--lucent-card-pad, var(--_lucent-card-pad, 16px 18px));
  box-shadow: var(--lucent-card-shadow, none);
  min-width: 0;
}
.prose {
  font-size: 14px;
  line-height: 1.65;
  color: var(--card-ink);
  /* Long URLs (markdown-it linkifies them) and unbroken tokens wrap
     instead of pushing the card wider than its column. */
  overflow-wrap: anywhere;
}
/* Tables, code blocks and images scroll or scale inside the card. */
.prose :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: 0 0 10px;
}
.prose :deep(th),
.prose :deep(td) {
  padding: 6px 10px;
  border-bottom: 1px solid var(--rule);
  text-align: left;
  overflow-wrap: normal;
}
.prose :deep(pre) {
  max-width: 100%;
  overflow-x: auto;
  margin: 0 0 10px;
  padding: 10px 12px;
  background: var(--rule-soft);
  border-radius: 6px;
}
.prose :deep(pre code) {
  border: 0;
  padding: 0;
  background: none;
  white-space: pre;
  overflow-wrap: normal;
}
.prose :deep(img) {
  max-width: 100%;
  height: auto;
}
.prose :deep(> :first-child) {
  margin-top: 0;
}
.prose :deep(> :last-child) {
  margin-bottom: 0;
}
.prose :deep(h1) {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  margin: 0 0 10px;
  line-height: 1.2;
}
.prose :deep(h2) {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.015em;
  margin: 18px 0 6px;
  line-height: 1.2;
}
.prose :deep(h3) {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 14px 0 4px;
  line-height: 1.2;
}
.prose :deep(p) {
  margin: 0 0 10px;
}
.prose :deep(ul),
.prose :deep(ol) {
  margin: 0 0 10px;
  padding-left: 22px;
}
.prose :deep(li) {
  margin: 2px 0;
}
.prose :deep(code) {
  font-family: var(--mono);
  font-size: 0.88em;
  background: var(--rule-soft);
  color: var(--card-ink);
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid var(--rule);
}
.prose :deep(a) {
  color: var(--primary-deep);
  text-decoration: none;
  border-bottom: 1px solid var(--primary);
  padding-bottom: 1px;
}
:global(html[data-theme="dark"]) .prose :deep(a) {
  color: var(--primary);
}
.prose :deep(strong) {
  font-weight: 600;
}
.prose :deep(blockquote) {
  margin: 12px 0;
  padding: 8px 14px;
  border-left: 2px solid var(--primary);
  color: var(--card-muted);
  font-style: italic;
}
/* Inline icon placeholder. The actual SVG is mounted by Vue at
   runtime — the placeholder establishes baseline alignment with the
   surrounding text so the icon doesn't kick the line height. */
.prose :deep(span.lucent-md-icon) {
  display: inline-flex;
  align-items: center;
  vertical-align: -0.18em;
  margin: 0 1px;
  color: currentColor;
  opacity: 0.85;
}
</style>
